/**
 * Return a Cypher snippet that resolves the equipment subtree +
 * reachable compute (formerly "gateway") for a given Plant scope
 * (line / process / equipment). Powers
 * `plantViewerApi.getPlantTagsByScope`.
 *
 * The returned query binds `$nodeId` to the canonical ontology id
 * and returns:
 *   - `n`        : the scope node itself (so the live-tag panel can
 *                  echo the display name)
 *   - `tags`     : distinct Tag / PLCTag / UDTInstance nodes
 *                  reachable from the scope via three independent
 *                  sources (deduped by node identity):
 *                  (1) the equipment property substructure
 *                  (Observable / Setpoint / EquipmentProperty
 *                  `MEASURED_BY` / `SET_BY` a Tag),
 *                  (2) the control layer (scope / subline /
 *                  equipment `CONTROLLED_BY` a Controller whose
 *                  PLCTags `MAPS_TO_TAG` a Tag), and
 *                  (3) the direct telemetry source (scope / subline
 *                  / equipment `MONITORED_BY` a Compute, surfacing
 *                  every Tag that `DERIVES_FROM` that Compute —
 *                  including external OPC servers that carry no
 *                  controller bridge).
 *   - `gateway`  : first Compute upstream via
 *                  `Site -[:HAS_COMPUTE]-> Compute`.
 *
 * The result key is named `gateway` for backwards compatibility
 * with the `PlantGatewayRef` shape consumed by the rest of the UI;
 * under the new ontology this is a `:Compute` node.
 *
 * Each tag source is collected inside its own `CALL { ... }`
 * subquery returning a single aggregated list, so the outer query
 * stays at one row per scope node. A `MAPS_TO_TAG` controller with
 * N tags and a `MONITORED_BY` compute with M tags would otherwise
 * cross-multiply into N*M rows.
 */
export function cypherForTagScope(scope: 'line' | 'process' | 'equipment'): string {
  switch (scope) {
    case 'line':
      // For line scope we gather tags from every equipment reachable from
      // the line OR any of its sublines (`HAS_SUBLINE*0..` keeps the line
      // itself when length is 0), plus the controller / MONITORED_BY edges on the
      // line, its sublines, or any equipment in the subtree.
      return `
        MATCH (n:Line {id: $nodeId})
        CALL {
          WITH n
          OPTIONAL MATCH (n)-[:HAS_SUBLINE*0..]->(s:Line)-[:HAS_EQUIPMENT*1..8]->(eq:Equipment)
          OPTIONAL MATCH (eq)-[:HAS_OBSERVABLE|HAS_PROPERTY|HAS_SETPOINT]->(prop)-[:MEASURED_BY|SET_BY]->(t)
            WHERE (t:Tag OR t:PLCTag OR t:UDTInstance)
          RETURN collect(DISTINCT t) AS propTags
        }
        CALL {
          WITH n
          OPTIONAL MATCH (n)-[:HAS_SUBLINE*0..]->(s:Line)
          OPTIONAL MATCH (s)-[:HAS_EQUIPMENT*1..8]->(eq:Equipment)
          WITH collect(DISTINCT s) + collect(DISTINCT eq) AS controlled
          UNWIND controlled AS c
          OPTIONAL MATCH (c)-[:CONTROLLED_BY]->(:Controller)-[:HAS_TAG]->(plc:PLCTag)-[:MAPS_TO_TAG]->(ctrlTag:Tag)
          RETURN collect(DISTINCT ctrlTag) AS controllerTags
        }
        CALL {
          WITH n
          OPTIONAL MATCH (n)-[:HAS_SUBLINE*0..]->(s:Line)
          OPTIONAL MATCH (s)-[:HAS_EQUIPMENT*1..8]->(eq:Equipment)
          WITH collect(DISTINCT s) + collect(DISTINCT eq) AS controlled
          UNWIND controlled AS c
          OPTIONAL MATCH (c)-[:MONITORED_BY]->(mc:Compute)<-[:DERIVES_FROM]-(monTag:Tag)
          RETURN collect(DISTINCT monTag) AS monitoredTags
        }
        WITH n, propTags + controllerTags + monitoredTags AS allTags
        UNWIND (CASE WHEN size(allTags) = 0 THEN [null] ELSE allTags END) AS tg
        WITH n, [x IN collect(DISTINCT tg) WHERE x IS NOT NULL] AS tags
        OPTIONAL MATCH (top:Line)-[:HAS_SUBLINE*0..]->(n)
          WHERE NOT (:Line)-[:HAS_SUBLINE]->(top)
        OPTIONAL MATCH (site:Site)-[:HAS_LINE]->(top)
        OPTIONAL MATCH (site)-[:HAS_COMPUTE]->(gw:Compute)
        RETURN n, tags, head(collect(gw)) AS gateway
      `
    case 'process':
      // Lines `INSTANTIATE` Processes (not the reverse). Resolve every line
      // that instantiates the process, then walk each line's (sub)line
      // subtree to its equipment.
      return `
        MATCH (n:Process {id: $nodeId})
        CALL {
          WITH n
          OPTIONAL MATCH (line:Line)-[:INSTANTIATES]->(n)
          OPTIONAL MATCH (line)-[:HAS_SUBLINE*0..]->(s:Line)-[:HAS_EQUIPMENT*1..8]->(eq:Equipment)
          OPTIONAL MATCH (eq)-[:HAS_OBSERVABLE|HAS_PROPERTY|HAS_SETPOINT]->(prop)-[:MEASURED_BY|SET_BY]->(t)
            WHERE (t:Tag OR t:PLCTag OR t:UDTInstance)
          RETURN collect(DISTINCT t) AS propTags
        }
        CALL {
          WITH n
          OPTIONAL MATCH (line:Line)-[:INSTANTIATES]->(n)
          OPTIONAL MATCH (line)-[:HAS_SUBLINE*0..]->(s:Line)
          OPTIONAL MATCH (s)-[:HAS_EQUIPMENT*1..8]->(eq:Equipment)
          WITH collect(DISTINCT s) + collect(DISTINCT eq) AS controlled
          UNWIND controlled AS c
          OPTIONAL MATCH (c)-[:CONTROLLED_BY]->(:Controller)-[:HAS_TAG]->(plc:PLCTag)-[:MAPS_TO_TAG]->(ctrlTag:Tag)
          RETURN collect(DISTINCT ctrlTag) AS controllerTags
        }
        CALL {
          WITH n
          OPTIONAL MATCH (line:Line)-[:INSTANTIATES]->(n)
          OPTIONAL MATCH (line)-[:HAS_SUBLINE*0..]->(s:Line)
          OPTIONAL MATCH (s)-[:HAS_EQUIPMENT*1..8]->(eq:Equipment)
          WITH collect(DISTINCT s) + collect(DISTINCT eq) AS controlled
          UNWIND controlled AS c
          OPTIONAL MATCH (c)-[:MONITORED_BY]->(mc:Compute)<-[:DERIVES_FROM]-(monTag:Tag)
          RETURN collect(DISTINCT monTag) AS monitoredTags
        }
        WITH n, propTags + controllerTags + monitoredTags AS allTags
        UNWIND (CASE WHEN size(allTags) = 0 THEN [null] ELSE allTags END) AS tg
        WITH n, [x IN collect(DISTINCT tg) WHERE x IS NOT NULL] AS tags
        OPTIONAL MATCH (line2:Line)-[:INSTANTIATES]->(n)
        OPTIONAL MATCH (top:Line)-[:HAS_SUBLINE*0..]->(line2)
          WHERE NOT (:Line)-[:HAS_SUBLINE]->(top)
        OPTIONAL MATCH (site:Site)-[:HAS_LINE]->(top)
        WITH n, tags, head(collect(DISTINCT site)) AS site
        OPTIONAL MATCH (site)-[:HAS_COMPUTE]->(gw:Compute)
        RETURN n, tags, head(collect(gw)) AS gateway
      `
    case 'equipment':
      return `
        MATCH (n:Equipment {id: $nodeId})
        CALL {
          WITH n
          OPTIONAL MATCH (n)-[:HAS_OBSERVABLE|HAS_PROPERTY|HAS_SETPOINT]->(prop)-[:MEASURED_BY|SET_BY]->(t)
            WHERE (t:Tag OR t:PLCTag OR t:UDTInstance)
          RETURN collect(DISTINCT t) AS propTags
        }
        CALL {
          WITH n
          OPTIONAL MATCH (n)-[:CONTROLLED_BY]->(:Controller)-[:HAS_TAG]->(plc:PLCTag)-[:MAPS_TO_TAG]->(ctrlTag:Tag)
          RETURN collect(DISTINCT ctrlTag) AS controllerTags
        }
        CALL {
          WITH n
          OPTIONAL MATCH (n)-[:MONITORED_BY]->(mc:Compute)<-[:DERIVES_FROM]-(monTag:Tag)
          RETURN collect(DISTINCT monTag) AS monitoredTags
        }
        WITH n, propTags + controllerTags + monitoredTags AS allTags
        UNWIND (CASE WHEN size(allTags) = 0 THEN [null] ELSE allTags END) AS tg
        WITH n, [x IN collect(DISTINCT tg) WHERE x IS NOT NULL] AS tags
        OPTIONAL MATCH (n)<-[:HAS_EQUIPMENT*1..8]-(line:Line)
        OPTIONAL MATCH (line)<-[:HAS_SUBLINE*0..20]-(:Line)<-[:HAS_LINE]-(site:Site)
        WITH n, tags, head(collect(site)) AS site
        OPTIONAL MATCH (site)-[:HAS_COMPUTE]->(gw:Compute)
          WHERE site IS NOT NULL
        RETURN n, tags, head(collect(gw)) AS gateway
      `
  }
}
